import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import LoadingSpinner from '../common/LoadingSpinner';
import type { ReasoningEffort, TenantAiConfig, TenantAiConfigUpdate } from '../../services/types';
import { formatGermanDateTime } from '../../utils/dateFormat';

/** Props der AiConfigForm-Komponente. */
export interface AiConfigFormProps {
  /** Geladene Konfiguration des gewaehlten Mandanten oder null (laedt noch, Fehler). */
  readonly config: TenantAiConfig | null;
  /** true, waehrend die Konfiguration geladen wird. */
  readonly loading: boolean;
  /** true, waehrend eine Aenderung gespeichert wird. */
  readonly saving: boolean;
  /** true, waehrend die Konfiguration geloescht wird. */
  readonly deleting: boolean;
  /** Deutsche Fehlermeldung des letzten Vorgangs oder null. */
  readonly error: string | null;
  /** Deutsche Erfolgsmeldung des letzten Vorgangs oder null. */
  readonly successMessage: string | null;
  /** Speichert eine Aenderung (Semantik siehe buildUpdate weiter unten). */
  readonly onSave: (update: TenantAiConfigUpdate) => void;
  /** Loescht Key, Modell und Prompt (nach Bestaetigung im Modal). */
  readonly onDelete: () => void;
  /** Blendet eine noch sichtbare Erfolgs-/Fehlermeldung aus. */
  readonly onClearMessages: () => void;
}

/** Maximale Laenge des Modellnamens (Backend-Vertrag). */
const MODEL_MAX_LENGTH = 200;

/** Maximale Laenge des Lagebeschreibungs-Prompts (Backend-Vertrag). */
const PROMPT_MAX_LENGTH = 20000;

/** Erlaubte Zeichen der Modell-ID (Backend-Vertrag). */
const MODEL_PATTERN = /^[A-Za-z0-9._:/-]+$/;

/** Ermittelt die deutsche Validierungsmeldung fuer das Modellfeld, oder null. */
function validateModel(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') {
    return 'Bitte ein Modell angeben.';
  }
  if (trimmed.length > MODEL_MAX_LENGTH) {
    return `Das Modell darf höchstens ${MODEL_MAX_LENGTH} Zeichen lang sein.`;
  }
  if (!MODEL_PATTERN.test(trimmed)) {
    return 'Das Modell darf nur Buchstaben, Ziffern sowie . _ : / - enthalten.';
  }
  return null;
}

/**
 * Ermittelt den zu sendenden `locationPrompt`-Wert:
 * - Eingabe leer UND bisher ein Prompt hinterlegt -> "" (entfernen)
 * - Eingabe leer UND bisher kein Prompt hinterlegt -> undefined (unveraendert)
 * - sonst die Eingabe selbst
 */
function buildLocationPromptField(promptInput: string, currentPrompt: string | null): string | undefined {
  if (promptInput === '') {
    return currentPrompt !== null ? '' : undefined;
  }
  return promptInput;
}

/**
 * Ermittelt den zu sendenden `apiKey`-Wert aus der GETRIMMTEN Eingabe:
 * eine leere (oder nur aus Leerzeichen bestehende) Eingabe bedeutet
 * "unveraendert lassen" (undefined), jede andere Eingabe wird gesendet.
 */
function buildApiKeyField(trimmedApiKeyInput: string): string | undefined {
  return trimmedApiKeyInput === '' ? undefined : trimmedApiKeyInput;
}

/**
 * Ermittelt den zu sendenden `reasoningEffort`-Wert:
 * - Auswahl "Modell entscheidet" (leer) UND bisher ein Wert hinterlegt -> "" (entfernen)
 * - Auswahl "Modell entscheidet" (leer) UND bisher kein Wert hinterlegt -> undefined (unveraendert)
 * - sonst die Auswahl selbst (LOW|MEDIUM|HIGH)
 */
function buildReasoningEffortField(
  selectValue: string,
  currentReasoningEffort: ReasoningEffort | null
): string | undefined {
  if (selectValue === '') {
    return currentReasoningEffort !== null ? '' : undefined;
  }
  return selectValue;
}

/**
 * Ermittelt den zu sendenden `webSearchMaxResults`-Wert:
 * - Websuche deaktiviert UND bisher ein Wert hinterlegt -> 0 (entfernen)
 * - Websuche deaktiviert UND bisher kein Wert hinterlegt -> undefined (unveraendert)
 * - Websuche aktiviert -> die eingegebene Trefferzahl
 */
function buildWebSearchField(
  webSearchEnabled: boolean,
  webSearchMaxResults: number,
  currentWebSearchMaxResults: number | null
): number | undefined {
  if (!webSearchEnabled) {
    return currentWebSearchMaxResults !== null ? 0 : undefined;
  }
  return webSearchMaxResults;
}

/**
 * Formular fuer die KI-Konfiguration eines Mandanten: API-Key-Status,
 * API-Key-Eingabe (nur bei Aenderung gesendet), Modell und
 * Lagebeschreibungs-Prompt.
 *
 * Kein optimistisches Update: Die lokalen Eingabefelder werden IMMER aus
 * der vom Elternteil uebergebenen `config` (dem vom Backend BESTAETIGTEN
 * Stand) neu befuellt, sobald sich deren Objektreferenz aendert — also
 * beim Laden, nach erfolgreichem Speichern und nach erfolgreichem
 * Loeschen. Waehrend der Bearbeitung (kein neuer `config`-Stand) bleiben
 * Tippfehler-Korrekturen des Nutzers unangetastet.
 */
function AiConfigForm({
  config,
  loading,
  saving,
  deleting,
  error,
  successMessage,
  onSave,
  onDelete,
  onClearMessages,
}: AiConfigFormProps): React.JSX.Element {
  const [modelInput, setModelInput] = useState<string>('');
  const [promptInput, setPromptInput] = useState<string>('');
  const [apiKeyInput, setApiKeyInput] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [modelTouched, setModelTouched] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [reasoningEffortInput, setReasoningEffortInput] = useState<string>('');
  const [webSearchEnabled, setWebSearchEnabled] = useState<boolean>(false);
  const [webSearchCountInput, setWebSearchCountInput] = useState<string>('3');
  // Wie modelTouched: Fehlerdarstellung erst nach Blur oder Speichern-Versuch, nicht mid-edit.
  const [webSearchCountTouched, setWebSearchCountTouched] = useState<boolean>(false);

  // config-Referenzwechsel = neuer bestaetigter Stand (Laden, Speichern,
  // Loeschen) -> lokale Eingabefelder neu befuellen (siehe Klassenkommentar).
  useEffect(() => {
    setModelInput(config?.model ?? '');
    setPromptInput(config?.locationPrompt ?? '');
    setApiKeyInput('');
    setShowApiKey(false);
    setModelTouched(false);
    setWebSearchCountTouched(false);
    setReasoningEffortInput(config?.reasoningEffort ?? '');
    const currentWebSearchMaxResults = config?.webSearchMaxResults ?? null;
    setWebSearchEnabled(currentWebSearchMaxResults !== null);
    setWebSearchCountInput(currentWebSearchMaxResults !== null ? String(currentWebSearchMaxResults) : '3');
  }, [config]);

  // Getrimmte Werte EINMAL ermittelt und danach durchgaengig fuer
  // Validierung, Dirty-Vergleich und Payload verwendet — sonst koennte
  // z. B. reines Whitespace-Anhaengen als "geaendert" gelten, obwohl der
  // gesendete (getrimmte) Wert identisch mit dem gespeicherten Stand ist.
  const trimmedModel = modelInput.trim();
  const trimmedApiKey = apiKeyInput.trim();
  const modelError = validateModel(trimmedModel);

  const currentReasoningEffort = config?.reasoningEffort ?? null;
  const currentWebSearchMaxResults = config?.webSearchMaxResults ?? null;
  const trimmedWebSearchCount = webSearchCountInput.trim();
  const webSearchCountNumber = trimmedWebSearchCount === '' ? Number.NaN : Number(trimmedWebSearchCount);
  const webSearchCountValid =
    Number.isInteger(webSearchCountNumber) && webSearchCountNumber >= 1 && webSearchCountNumber <= 10;
  const webSearchInvalid = webSearchEnabled && !webSearchCountValid;

  const dirty =
    trimmedModel !== (config?.model ?? '') ||
    promptInput !== (config?.locationPrompt ?? '') ||
    trimmedApiKey !== '' ||
    reasoningEffortInput !== (currentReasoningEffort ?? '') ||
    (webSearchEnabled
      ? currentWebSearchMaxResults !== webSearchCountNumber
      : currentWebSearchMaxResults !== null);
  const canDelete =
    config !== null &&
    (config.apiKeyConfigured ||
      config.model !== null ||
      config.locationPrompt !== null ||
      config.reasoningEffort !== null ||
      config.webSearchMaxResults !== null);
  const busy = saving || deleting;

  const handleSave = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setModelTouched(true);
    setWebSearchCountTouched(true);
    if (modelError !== null || webSearchInvalid || busy || !dirty || config === null) {
      return;
    }
    onSave({
      apiKey: buildApiKeyField(trimmedApiKey),
      model: trimmedModel,
      locationPrompt: buildLocationPromptField(promptInput, config.locationPrompt),
      reasoningEffort: buildReasoningEffortField(reasoningEffortInput, config.reasoningEffort),
      webSearchMaxResults: buildWebSearchField(webSearchEnabled, webSearchCountNumber, config.webSearchMaxResults),
    });
  };

  const handleConfirmDelete = (): void => {
    setShowDeleteModal(false);
    onDelete();
  };

  if (loading) {
    return (
      <div id="ai-config-form-loading-container" className="d-flex justify-content-center py-4">
        <LoadingSpinner id="ai-config-form-loading" />
      </div>
    );
  }

  if (config === null) {
    return (
      <Alert id="ai-config-form-error" variant="danger" className="mb-0">
        {error ?? 'Die KI-Konfiguration konnte nicht geladen werden.'}
      </Alert>
    );
  }

  return (
    <Form id="ai-config-form" noValidate onSubmit={handleSave}>
      {error !== null && (
        <Alert
          id="ai-config-error"
          variant="danger"
          dismissible
          onClose={onClearMessages}
          className="mb-3"
        >
          {error}
        </Alert>
      )}
      {successMessage !== null && (
        <Alert
          id="ai-config-success"
          variant="success"
          dismissible
          onClose={onClearMessages}
          className="mb-3"
        >
          {successMessage}
        </Alert>
      )}

      <div id="ai-config-api-key-status-row" className="mb-3">
        <div id="ai-config-api-key-status" className="fw-medium">
          {config.apiKeyConfigured
            ? config.apiKeyHint !== null
              ? `API-Key hinterlegt (${config.apiKeyHint})`
              : 'API-Key hinterlegt (kein Hinweis verfügbar)'
            : 'Kein API-Key hinterlegt'}
        </div>
        <div id="ai-config-updated-at" className="small text-muted">
          {`Letzte Änderung: ${formatGermanDateTime(config.updatedAt)}`}
        </div>
      </div>

      {/*
        Die folgenden drei Form.Group-Bloecke setzen bewusst KEIN
        `controlId`: In Kombination mit einer eigenen `id` auf dem
        jeweiligen Form.Control erzeugt react-bootstrap sonst eine
        Konsolen-Warnung ("controlId is ignored on <FormLabel>..."). Die
        Verbindung Label/Control laeuft stattdessen ueber `htmlFor`; die
        HTML-IDs der Controls bleiben unveraendert.
      */}
      <Form.Group id="ai-config-api-key-group" className="mb-3">
        <Form.Label id="ai-config-api-key-label" htmlFor="ai-config-api-key-input">
          OpenRouter-API-Key
        </Form.Label>
        <InputGroup id="ai-config-api-key-input-group">
          <Form.Control
            id="ai-config-api-key-input"
            type={showApiKey ? 'text' : 'password'}
            autoComplete="off"
            placeholder={config.apiKeyConfigured ? 'Unverändert lassen' : 'API-Key eingeben'}
            value={apiKeyInput}
            disabled={busy}
            onChange={(event) => setApiKeyInput(event.target.value)}
          />
          <Button
            id="ai-config-api-key-toggle"
            variant="outline-secondary"
            type="button"
            disabled={busy}
            onClick={() => setShowApiKey((previous) => !previous)}
            title={showApiKey ? 'API-Key verbergen' : 'API-Key anzeigen'}
          >
            <i id="ai-config-api-key-toggle-icon" className={`bi ${showApiKey ? 'bi-eye-slash' : 'bi-eye'}`}></i>
          </Button>
        </InputGroup>
        <Form.Text id="ai-config-api-key-help" muted>
          Wird leer gelassen, bleibt ein bereits hinterlegter Key unverändert.
        </Form.Text>
      </Form.Group>

      <Form.Group id="ai-config-model-group" className="mb-3">
        <Form.Label id="ai-config-model-label" htmlFor="ai-config-model-input">
          Modell
        </Form.Label>
        <Form.Control
          id="ai-config-model-input"
          type="text"
          value={modelInput}
          disabled={busy}
          maxLength={MODEL_MAX_LENGTH}
          isInvalid={modelTouched && modelError !== null}
          onChange={(event) => setModelInput(event.target.value)}
          onBlur={() => setModelTouched(true)}
        />
        <Form.Control.Feedback id="ai-config-model-feedback" type="invalid">
          {modelError}
        </Form.Control.Feedback>
        <Form.Text id="ai-config-model-help" muted>
          OpenRouter-Modell-ID, z. B. meta/muse-spark-1.3-contributor
        </Form.Text>
      </Form.Group>

      <Form.Group id="ai-config-prompt-group" className="mb-3">
        <Form.Label id="ai-config-prompt-label" htmlFor="ai-config-prompt-textarea">
          Lagebeschreibungs-Prompt
        </Form.Label>
        <Form.Control
          id="ai-config-prompt-textarea"
          as="textarea"
          rows={14}
          value={promptInput}
          disabled={busy}
          maxLength={PROMPT_MAX_LENGTH}
          onChange={(event) => setPromptInput(event.target.value)}
        />
        <div id="ai-config-prompt-counter" className="small text-muted text-end">
          {`${promptInput.length} / ${PROMPT_MAX_LENGTH}`}
        </div>
        <Form.Text id="ai-config-prompt-help" muted>
          Ersetzt nur den Leitfaden-Teil des System-Prompts; Sicherheits- und Ausgabevorgaben
          bleiben fest. Leer lassen = Standard-Leitfaden.
        </Form.Text>
      </Form.Group>

      <Form.Group id="ai-config-reasoning-group" className="mb-3">
        <Form.Label id="ai-config-reasoning-effort-label" htmlFor="ai-config-reasoning-effort-select">
          Reasoning-Aufwand
        </Form.Label>
        <Form.Select
          id="ai-config-reasoning-effort-select"
          value={reasoningEffortInput}
          disabled={busy}
          onChange={(event) => setReasoningEffortInput(event.target.value)}
        >
          <option value="">Modell entscheidet (nicht senden)</option>
          <option value="LOW">Niedrig</option>
          <option value="MEDIUM">Mittel</option>
          <option value="HIGH">Hoch</option>
        </Form.Select>
        <Form.Text id="ai-config-reasoning-effort-help" muted>
          Erzwingt bei Reasoning-Modellen den Denkaufwand. Reasoning-Tokens zählen gegen das
          Token-Budget des Backends; die Denkschritte werden nie in die Antwort übernommen.
        </Form.Text>
      </Form.Group>

      <Form.Group id="ai-config-web-search-group" className="mb-3">
        <Form.Check
          id="ai-config-web-search-checkbox"
          type="checkbox"
          label="Websuche aktivieren (OpenRouter Web-Plugin)"
          checked={webSearchEnabled}
          disabled={busy}
          onChange={(event) => setWebSearchEnabled(event.target.checked)}
        />
        <Form.Label
          id="ai-config-web-search-max-results-label"
          htmlFor="ai-config-web-search-max-results-input"
          className="mt-2"
        >
          Maximale Suchtreffer
        </Form.Label>
        <Form.Control
          id="ai-config-web-search-max-results-input"
          type="number"
          min={1}
          max={10}
          value={webSearchCountInput}
          disabled={busy || !webSearchEnabled}
          isInvalid={webSearchCountTouched && webSearchInvalid}
          onChange={(event) => setWebSearchCountInput(event.target.value)}
          onBlur={() => setWebSearchCountTouched(true)}
        />
        <Form.Control.Feedback id="ai-config-web-search-feedback" type="invalid">
          Bitte 1 bis 10 Suchtreffer angeben.
        </Form.Control.Feedback>
        <Form.Text id="ai-config-web-search-help" muted>
          Das Modell recherchiert Fakten zur Lage (Haltestellen, Läden, Schulen). Die Websuche
          verursacht zusätzliche Such- und Tokenkosten je Aufruf (Höhe abhängig von Modell und
          Suchanbieter, Stand 09/2026 etwa das 30-Fache eines Aufrufs ohne Suche); die Adresse der
          Immobilie wird dabei an den Suchanbieter von OpenRouter übermittelt.
        </Form.Text>
      </Form.Group>

      <div id="ai-config-actions" className="d-grid gap-2 d-sm-flex justify-content-sm-end">
        <Button
          id="ai-config-delete-button"
          type="button"
          variant="outline-danger"
          disabled={!canDelete || busy}
          onClick={() => setShowDeleteModal(true)}
        >
          {deleting && <Spinner id="ai-config-delete-spinner" animation="border" size="sm" className="me-2" />}
          Konfiguration löschen
        </Button>
        <Button
          id="ai-config-save-button"
          type="submit"
          variant="primary"
          disabled={busy || !dirty || modelError !== null || webSearchInvalid}
        >
          {saving && <Spinner id="ai-config-save-spinner" animation="border" size="sm" className="me-2" />}
          Speichern
        </Button>
      </div>

      <Modal
        id="ai-config-delete-modal"
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        aria-labelledby="ai-config-delete-modal-title"
      >
        <Modal.Header id="ai-config-delete-modal-header" closeButton>
          <Modal.Title id="ai-config-delete-modal-title" className="h6 mb-0">
            Konfiguration löschen?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body id="ai-config-delete-modal-body">
          API-Key, Modell, Lagebeschreibungs-Prompt, Reasoning-Einstellung und
          Websuche-Konfiguration dieses Mandanten werden entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
        </Modal.Body>
        <Modal.Footer id="ai-config-delete-modal-footer">
          <Button
            id="ai-config-delete-cancel-button"
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Abbrechen
          </Button>
          <Button id="ai-config-delete-confirm-button" variant="danger" onClick={handleConfirmDelete}>
            Löschen
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
}

export default AiConfigForm;
